import { Button } from 'reactstrap';
import { CampaignHeaderSection } from '~/shared/components/cards/CampaignItemsCard';
import ExpensesFilters from './FundRequestsFilters';
import ExpensesTable from './FundRequestsTable';
import { useContext, useEffect, useState } from 'react';
import { deleteFundRequest, fetchFundRequests } from '~/services/fundrequests';
import { fundRequestListing } from '~/types/fundrequests';
import { AdminContext } from '~/context/admin';
import { CampaignContext } from '~/context/campaign';
import { FaExclamationCircle } from 'react-icons/fa';
import { ConfirmModal } from '~/shared/components/ConfirmModal';
import { StatusLegendModal } from './StatusLegendModal';
import { renderIcon } from '~/shared/components/icons';
import { useHistory } from 'react-router-dom';

function FundRequestHeading() {
    const history = useHistory();
    return (
        <div>
            <h1 className='h2'>Fund Requests</h1>
            <div>
                <Button
                    color='primary'
                    size='sm'
                    onClick={() => history.push('/requests/request/submit')}
                >
                    {renderIcon('add')} New Request
                </Button>
            </div>
        </div>
    );
}

const filters = [
    {
        label: 'All Expenses',
        value: '',
    },
    {
        label: 'Draft',
        value: 'draft',
    },
    {
        label: 'Submitted',
        value: 'submitted',
    },
    {
        label: 'In Review',
        value: 'ap_review',
    },
    {
        label: 'Approved',
        value: 'approved',
    },
    {
        label: 'Paid',
        value: 'paid',
    },
    {
        label: 'Needs Info',
        value: 'needs_info',
    },
    {
        label: 'Resubmitted',
        value: 'resubmitted',
    },
    { label: 'Rejected', value: 'rejected' },
];

const FundRequests = () => {
    const [fundRequests, setFundRequests] = useState<fundRequestListing[]>([]);
    const [{ campaignId }, adminDispatch] = useContext(AdminContext);
    const [{ campaign }] = useContext(CampaignContext);
    const directVendorPayOnly =
        campaign?.campaignDetail?.direct_vendor_pay_only;
    const [currentFilter, setCurrentFilter] = useState<{
        label: string;
        value: string;
    }>(filters[0]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [isLoading, setIsLoading] = useState(false);
    const [deletingFundRequest, setDeletingFundRequest] =
        useState<fundRequestListing | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [openModal, setOpenModal] = useState<boolean>(false);
    const [sortBy, setSortBy] = useState<string>('');
    const [sortOrder, setSortOrder] = useState<string>('');

    const getFundRequests = async (
        page = 1,
        pageSize: number,
        status: string,
        sort_by?: string,
        sort_order?: string
    ) => {
        try {
            setIsLoading(true);
            const fundRequestList = await fetchFundRequests(
                { page, pageSize, status, sort_by, sort_order },
                campaignId
            );
            setFundRequests(fundRequestList?.results || []);
            setTotalPages(Math.ceil(fundRequestList.count / pageSize) || 1);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        getFundRequests(
            currentPage,
            pageSize,
            currentFilter.value,
            sortBy,
            sortOrder
        );
    }, [currentPage, pageSize, currentFilter, campaignId, sortBy, sortOrder]);

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const handleDeleteFundRequest = async () => {
        try {
            if (!deletingFundRequest) {
                return;
            }
            setIsDeleting(true);
            await deleteFundRequest(deletingFundRequest.id, campaignId);
            setDeletingFundRequest(null);
            getFundRequests(
                currentPage,
                pageSize,
                currentFilter.value,
                sortBy,
                sortOrder
            );
            setIsDeleting(false);
        } catch (error) {
            console.log('error: ', error);
            setIsDeleting(false);
        }
    };

    const handleSort = (columnId: string, direction: 'asc' | 'desc') => {
        setSortBy(columnId);
        setSortOrder(direction);
        setCurrentPage(1); // Reset to first page when sorting
    };

    return (
        <>
            <div className='container__inner'>
                <CampaignHeaderSection
                    leftcard={<FundRequestHeading />}
                    banner={
                        directVendorPayOnly ? (
                            <div
                                className='h-100 d-flex align-items-center rounded p-3 fw-bold'
                                style={{ backgroundColor: '#fff9e6' }}
                            >
                                Reimbursements to this campaign are not allowed.
                                Direct payments to payees only.
                            </div>
                        ) : undefined
                    }
                />
                <ExpensesFilters
                    currentFilter={currentFilter}
                    setCurrentFilter={setCurrentFilter}
                    filters={filters}
                />
                <div className='d-flex flex-wrap align-items-center gap-3 mt-4'>
                    <div
                        className='text-primary fw-bold px-3 py-2 rounded-pill d-inline-flex align-items-center gap-1'
                        style={{ backgroundColor: '#f5fbfc', cursor: 'pointer' }}
                        onClick={() => setOpenModal(!openModal)}
                        role='button'
                        tabIndex={0}
                    >
                        <FaExclamationCircle color='black' /> What do these{' '}
                        <span className='badge-submitted badge bg-none'>
                            Statuses
                        </span>{' '}
                        mean?
                    </div>
                    <p
                        className='mb-0 px-3 py-2 rounded'
                        style={{ backgroundColor: '#fff9e6' }}
                    >
                        Please allow <strong>5-8 business days</strong> after the submitted date for Help Hope Live to process your request.
                    </p>
                </div>
                <ExpensesTable
                    fundRequests={fundRequests}
                    currentPage={currentPage}
                    totalPages={totalPages}
                    pageSize={pageSize}
                    setPageSize={setPageSize}
                    onPageChange={handlePageChange}
                    isLoading={isLoading}
                    setDeletingFundRequest={setDeletingFundRequest}
                    onSort={handleSort}
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                />
            </div>
            <ConfirmModal
                isOpen={deletingFundRequest !== null}
                toggle={() => {
                    setDeletingFundRequest(null);
                }}
                title='Delete Fund Request'
                message='Are you sure you want to delete this fund request?'
                handleConfirm={() => {
                    handleDeleteFundRequest();
                }}
                saving={isDeleting}
                isDeleting={true}
            />
            <StatusLegendModal
                isOpen={openModal}
                toggle={() => setOpenModal(!openModal)}
            />
        </>
    );
};

export default FundRequests;
